import { create } from "zustand";
import { supabase } from "../lib/supabase";

interface ProfileStats {
  countriesCount: number;
  tripsCount: number;
  spotsCount: number;
  friendsCount: number;
  countries: string[];
  photosCount: number;
}

interface Badge {
  id: string;
  name: string;
  description: string;
  icon: string;
  unlocked: boolean;
  unlockedAt?: string;
  category: "milestone" | "category";
}

// Badge definitions
const BADGE_DEFINITIONS: Omit<Badge, "unlocked">[] = [
  // Milestone badges
  {
    id: "first-trip",
    name: "First Trip",
    description: "Created your first trip",
    icon: "🎒",
    category: "milestone",
  },
  {
    id: "five-countries",
    name: "Globe Trotter",
    description: "Visited 5 countries",
    icon: "🌍",
    category: "milestone",
  },
  {
    id: "ten-trips",
    name: "Seasoned Traveler",
    description: "Completed 10 trips",
    icon: "✈️",
    category: "milestone",
  },
  {
    id: "fifty-spots",
    name: "Explorer",
    description: "Logged 50 spots",
    icon: "📍",
    category: "milestone",
  },
  {
    id: "first-photo",
    name: "Shutterbug",
    description: "Uploaded first photo",
    icon: "📸",
    category: "milestone",
  },
  // Category badges
  {
    id: "foodie",
    name: "Foodie",
    description: "Visited 10 restaurants",
    icon: "🍜",
    category: "category",
  },
  {
    id: "culture-vulture",
    name: "Culture Vulture",
    description: "Visited 5 museums",
    icon: "🏛️",
    category: "category",
  },
  {
    id: "nature-lover",
    name: "Nature Lover",
    description: "Visited 5 parks",
    icon: "🌲",
    category: "category",
  },
];

interface ProfileState {
  stats: ProfileStats | null;
  badges: Badge[];
  manualCountries: string[];
  loading: boolean;
  error: string | null;

  fetchProfileStats: (userId: string) => Promise<void>;
  addManualCountry: (country: string) => Promise<void>;
  removeManualCountry: (country: string) => Promise<void>;
  clearError: () => void;
}

export const useProfileStore = create<ProfileState>((set, get) => ({
  stats: null,
  badges: [],
  manualCountries: [],
  loading: false,
  error: null,

  fetchProfileStats: async (userId: string) => {
    set({ loading: true, error: null });
    try {
      // Run all queries in parallel to avoid one slow query blocking the rest
      const [
        countriesResult,
        tripsResult,
        spotsResult,
        photosResult,
        friendsResult,
        categoriesResult,
        manualCountriesResult,
      ] = await Promise.all([
        supabase
          .from("places")
          .select("country")
          .eq("user_id", userId)
          .not("country", "is", null),
        supabase
          .from("lists")
          .select("*", { count: "exact", head: true })
          .eq("user_id", userId),
        supabase
          .from("places")
          .select("*", { count: "exact", head: true })
          .eq("user_id", userId),
        supabase
          .from("photos")
          .select("*", { count: "exact", head: true })
          .eq("user_id", userId),
        supabase
          .from("follows")
          .select("*", { count: "exact", head: true })
          .eq("follower_id", userId),
        supabase
          .from("places")
          .select("category")
          .eq("user_id", userId),
        supabase
          .from("places")
          .select("country")
          .eq("user_id", userId)
          .eq("category", "country-manual"),
      ]);

      // Log per-query errors for debugging
      if (countriesResult.error) console.warn("Failed to fetch countries:", countriesResult.error);
      if (tripsResult.error) console.warn("Failed to fetch trips count:", tripsResult.error);
      if (spotsResult.error) console.warn("Failed to fetch spots count:", spotsResult.error);
      if (photosResult.error) console.warn("Failed to fetch photos count:", photosResult.error);
      if (friendsResult.error) console.warn("Failed to fetch friends count:", friendsResult.error);
      if (categoriesResult.error) console.warn("Failed to fetch categories:", categoriesResult.error);
      if (manualCountriesResult.error) console.warn("Failed to fetch manual countries:", manualCountriesResult.error);

      const uniqueCountries = [
        ...new Set(countriesResult.data?.map((p) => p.country).filter(Boolean)),
      ] as string[];

      const stats: ProfileStats = {
        countriesCount: uniqueCountries.length,
        tripsCount: tripsResult.count || 0,
        spotsCount: spotsResult.count || 0,
        friendsCount: friendsResult.count || 0,
        countries: uniqueCountries,
        photosCount: photosResult.count || 0,
      };

      const places = categoriesResult.data;

      // Compute badges
      const categoryCount: Record<string, number> = {};
      places?.forEach((p) => {
        if (p.category) {
          categoryCount[p.category] = (categoryCount[p.category] || 0) + 1;
        }
      });

      const badges: Badge[] = BADGE_DEFINITIONS.map((def) => {
        let unlocked = false;

        switch (def.id) {
          case "first-trip":
            unlocked = stats.tripsCount >= 1;
            break;
          case "five-countries":
            unlocked = stats.countriesCount >= 5;
            break;
          case "ten-trips":
            unlocked = stats.tripsCount >= 10;
            break;
          case "fifty-spots":
            unlocked = stats.spotsCount >= 50;
            break;
          case "first-photo":
            unlocked = stats.photosCount >= 1;
            break;
          case "foodie":
            unlocked = (categoryCount["restaurant"] || 0) >= 10;
            break;
          case "culture-vulture":
            unlocked = (categoryCount["museum"] || 0) >= 5;
            break;
          case "nature-lover":
            unlocked = (categoryCount["park"] || 0) >= 5;
            break;
        }

        return { ...def, unlocked };
      });

      const manualCountries = [
        ...new Set(
          manualCountriesResult.data
            ?.map((p) => p.country)
            .filter(Boolean) as string[]
        ),
      ];

      set({ stats, badges, manualCountries, loading: false });
    } catch (error) {
      const message = "Failed to fetch profile stats";
      console.error(message, error);
      set({ error: message, loading: false });
    }
  },

  addManualCountry: async (country: string) => {
    set({ error: null });
    try {
      const { data: authData, error: authError } =
        await supabase.auth.getUser();
      if (authError || !authData.user) {
        set({ error: "You must be logged in to add a country" });
        return;
      }

      const currentCountries = get().stats?.countries || [];
      if (currentCountries.includes(country)) {
        set({ error: `${country} is already in your travel stamps` });
        return;
      }

      const { error: insertError } = await supabase.from("places").insert({
        user_id: authData.user.id,
        name: country,
        country,
        latitude: 0,
        longitude: 0,
        category: "country-manual",
      });

      if (insertError) {
        set({ error: "Failed to add country" });
        return;
      }

      await get().fetchProfileStats(authData.user.id);
    } catch (error) {
      console.error("Failed to add manual country:", error);
      set({ error: "Failed to add country" });
    }
  },

  removeManualCountry: async (country: string) => {
    set({ error: null });
    try {
      const { data: authData, error: authError } =
        await supabase.auth.getUser();
      if (authError || !authData.user) {
        set({ error: "You must be logged in to remove a country" });
        return;
      }

      const { error: deleteError } = await supabase
        .from("places")
        .delete()
        .eq("user_id", authData.user.id)
        .eq("country", country)
        .eq("category", "country-manual");

      if (deleteError) {
        set({ error: "Failed to remove country" });
        return;
      }

      await get().fetchProfileStats(authData.user.id);
    } catch (error) {
      console.error("Failed to remove manual country:", error);
      set({ error: "Failed to remove country" });
    }
  },

  clearError: () => set({ error: null }),
}));
